import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { BackgroundProvider } from "@/contexts/BackgroundContext";
import { RootLayout } from "@/components/common/RootLayout";
import { MainPage } from "@/routes/MainPage";
import { IRLandingPage } from "@/routes/ir/IRLandingPage";
import { DataroomIndexPage } from "@/routes/ir/DataroomIndexPage";
import { NDAPage } from "@/routes/ir/NDAPage";
import { DocumentListPage } from "@/routes/ir/DocumentListPage";
import { DocumentViewerPage } from "@/routes/ir/DocumentViewerPage";
import { PortalPlaceholder } from "@/routes/ir/PortalPlaceholder";
import { IRPasswordGate } from "@/components/ir/IRPasswordGate";
import { FundComplianceAccessGate } from "@/components/fund-compliance/FundComplianceAccessGate";
import { FundComplianceAgentPage } from "@/routes/demo/FundComplianceAgentPage";
import { PlatformPage } from "@/routes/PlatformPage";
import { ProductsPage } from "@/routes/ProductsPage";
import { AboutPage } from "@/routes/AboutPage";
import { InsightsPage } from "@/routes/InsightsPage";
import { InsightDetailPage } from "@/routes/InsightDetailPage";
// Admin routes (/admin/insights*) are intentionally absent in the v1 archive
// build — it is a frontend-only static snapshot with no /api backend and no
// Cloudflare Access protection, so the editor must not ship at all.
import { ContactPage } from "@/routes/ContactPage";
import { CareersPage } from "@/routes/CareersPage";
import { LegalPage } from "@/routes/LegalPage";
import { NotFoundPage } from "@/routes/NotFoundPage";
import { LinkedInBannerPage } from "@/routes/gen/LinkedInBannerPage";
import { LinkedInPreSeedPage } from "@/routes/gen/LinkedInPreSeedPage";
import { SocialBackgroundPage } from "@/routes/gen/SocialBackgroundPage";
import { OgImagePage } from "@/routes/OgImagePage";
import { ArticleOgPage } from "@/routes/ArticleOgPage";

function BlogRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/insights/${slug ?? ""}`} replace />;
}

export default function App() {
  return (
    <ThemeProvider>
      <BackgroundProvider>
        <Routes>
          <Route path="/og-image" element={<OgImagePage />} />
          <Route path="/article-og" element={<ArticleOgPage />} />
          <Route path="/gen/linkedin-banner" element={<LinkedInBannerPage />} />
          <Route
            path="/gen/linkedin-preseed"
            element={<LinkedInPreSeedPage />}
          />
          <Route path="/gen/social-bg" element={<SocialBackgroundPage />} />
          <Route element={<RootLayout />}>
            <Route path="/" element={<MainPage />} />
            <Route path="/ir" element={<IRLandingPage />} />
            <Route path="/ir" element={<IRPasswordGate />}>
              <Route path="dataroom" element={<DataroomIndexPage />} />
              <Route path="dataroom/nda" element={<NDAPage />} />
              <Route path="dataroom/docs">
                <Route index element={<DocumentListPage />} />
                <Route path=":docId" element={<DocumentViewerPage />} />
              </Route>
              <Route path="portal" element={<PortalPlaceholder />} />
            </Route>
            <Route path="/demo">
              <Route
                path="fund-compliance-agent"
                element={<FundComplianceAccessGate />}
              >
                <Route index element={<FundComplianceAgentPage />} />
              </Route>
            </Route>
            <Route
              path="/fund-compliance-agent"
              element={<Navigate to="/demo/fund-compliance-agent" replace />}
            />
            <Route path="/platform" element={<PlatformPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/insights/:slug" element={<InsightDetailPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/legal" element={<LegalPage />} />
            <Route
              path="/disclaimer"
              element={<Navigate to="/legal#disclosure" replace />}
            />
            <Route path="/blog/:slug" element={<BlogRedirect />} />
            <Route path="/blog" element={<Navigate to="/insights" replace />} />
            <Route
              path="/investors"
              element={<Navigate to="/ir/dataroom/docs/CX-002-A" replace />}
            />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </BackgroundProvider>
    </ThemeProvider>
  );
}
